/**
 * Píxel de Meta: mide el embudo de la publicidad.
 *
 * Sin esto no hay forma de saber cuántos llegaron del anuncio, cuántos
 * se registraron, cuántos canjearon el código ni cuántos volvieron — ni
 * de reimpactar a quien entró y no se registró. Es lo primero que se
 * instala en un embudo pagado.
 *
 * El identificador va por variable de entorno y no incrustado en el
 * código, para poder cambiarlo o apagarlo sin recompilar a mano.
 *
 * Si no hay identificador configurado, todo esto no hace nada: no se
 * carga ningún script de Meta y no se manda ni un dato. Así, en
 * desarrollo y en pruebas no se ensucian las estadísticas reales.
 */

const ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean };
    _fbq?: unknown;
  }
}

let iniciado = false;

/** Carga el píxel una sola vez. Llamar al arrancar la app. */
export function iniciarPixel(): void {
  if (iniciado || !ID || typeof window === 'undefined') return;
  iniciado = true;

  /* eslint-disable */
  // Fragmento oficial de Meta, con la cola de eventos para que no se
  // pierda lo que ocurra antes de que termine de cargar el script.
  (function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq?.('init', ID);
  window.fbq?.('track', 'PageView');
}

/** Evento estándar de Meta (PageView, CompleteRegistration, etc.). */
export function evento(nombre: string, datos?: Record<string, unknown>): void {
  if (!ID) return;
  window.fbq?.('track', nombre, datos);
}

/** Evento propio, para lo que no encaja en los estándar de Meta. */
export function eventoPropio(nombre: string, datos?: Record<string, unknown>): void {
  if (!ID) return;
  window.fbq?.('trackCustom', nombre, datos);
}

// ---------------------------------------------------------------------
// Los tres momentos del embudo que importan
// ---------------------------------------------------------------------

/** Se registró. Es la conversión principal del anuncio. */
export function midioRegistro(origen?: string): void {
  evento('CompleteRegistration', {
    content_name: 'registro',
    status: 'completado',
    ...(origen ? { source: origen } : {}),
  });
}

/**
 * Canjeó un código y ganó los 3 meses.
 *
 * Es el evento que de verdad dice si la campaña funcionó: significa que
 * la persona fue hasta Instagram o Facebook a buscar el código, que es
 * el objetivo real del canje.
 */
export function midioCanje(campana?: string): void {
  eventoPropio('CanjeoCodigo', campana ? { campana } : undefined);
}

/** Anotó algo por primera vez: la señal de que la app le sirvió. */
export function midioPrimerRegistroDeBebe(): void {
  eventoPropio('PrimerRegistroBebe');
}
